import { useState, useEffect } from 'react'
import MetricCard from '../components/MetricCard'
import KnowledgeSourceTable from '../components/KnowledgeSourceTable'
import AddSourceModal from '../components/AddSourceModal'
import AddVersionModal from '../components/AddVersionModal'
import SourceDetailsModal from '../components/SourceDetailsModal'
import ConfirmDialog from '../components/ConfirmDialog'
import Icon from '../components/Icon'
import {
  fetchKnowledgeSources,
  fetchKnowledgeProducts,
  addKnowledgeSource,
  addKnowledgeVersion,
  uploadDocumentFile,
  updateKnowledgeSource,
  deleteKnowledgeVersion,
  deleteKnowledgeSource,
} from '../api/knowledge'
import { productVersionLabel, statusSeverity, deleteAffordance } from '../components/sourceVisuals'
import styles from './Knowledge.module.css'

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'document', label: 'Documents' },
  { key: 'web', label: 'Web Sources' },
]

// A content-set in one of these states is still moving, so the page keeps
// re-listing until they all settle.
const IN_FLIGHT_STATUSES = ['pending', 'processing', 'pending_delete']

export default function Knowledge() {
  const [sources, setSources] = useState([])
  const [stats, setStats] = useState({ total: 0, documents: 0, web: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Tenant's product catalog — the association picker's options and the
  // Products filter. A failed fetch just leaves this empty rather than
  // blocking the page.
  const [products, setProducts] = useState(/** @type {string[]} */ ([]))

  const [tab, setTab] = useState('all')
  const [productFilter, setProductFilter] = useState(/** @type {string} */ ('all'))
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sortKey, setSortKey] = useState('updatedAt')
  const [sortDir, setSortDir] = useState(/** @type {'asc'|'desc'} */ ('desc'))

  const [addOpen, setAddOpen] = useState(false)
  // Modal targets are held by id, not by object, so the open modal follows the
  // refreshed row instead of showing whatever the list looked like when it was
  // opened — content-sets change under it while a version ingests.
  const [detailsId, setDetailsId] = useState(/** @type {number|null} */ (null))
  const [addVersionId, setAddVersionId] = useState(/** @type {number|null} */ (null))
  const [deleteTarget, setDeleteTarget] = useState(
    /** @type {{source: object, contentSet?: object}|null} */ (null),
  )

  const [expandedIds, setExpandedIds] = useState(/** @type {number[]} */ ([]))

  // Type tab + product filter are applied server-side; free-text search is client-side.
  function load() {
    return fetchKnowledgeSources({
      sourceType: tab !== 'all' ? tab : undefined,
      product: productFilter !== 'all' ? productFilter : undefined,
    })
      .then(data => {
        setSources(data.sources ?? [])
        setStats(data.stats ?? { total: 0, documents: 0, web: 0 })
        setError(null)
      })
      .catch(e => setError(e.message))
  }

  useEffect(() => {
    load().finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, productFilter])

  useEffect(() => {
    fetchKnowledgeProducts().then(setProducts).catch(() => {})
  }, [])

  // Poll for the status flip while any content-set is still indexing or being removed.
  const hasInFlight = sources.some(
    s => (s.contentSets ?? []).some(cs => IN_FLIGHT_STATUSES.includes(cs.status)),
  )
  useEffect(() => {
    if (!hasInFlight) return
    const id = setInterval(load, 5000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInFlight, tab, productFilter])

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 300)
    return () => clearTimeout(id)
  }, [search])

  const productOptions = [...products].sort((a, b) => a.localeCompare(b))

  // Reset the filter if its product disappeared upstream, so the list doesn't
  // stay filtered against a product that no longer exists.
  useEffect(() => {
    if (productFilter !== 'all' && !productOptions.includes(productFilter)) {
      setProductFilter('all')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productOptions, productFilter])

  const filtered = sources.filter(s => {
    if (debouncedSearch) {
      const versions = (s.contentSets ?? []).map(cs => productVersionLabel(cs.productVersion)).join(' ')
      const haystack = `${s.name} ${(s.products ?? []).join(' ')} ${s.url} ${versions}`.toLowerCase()
      if (!haystack.includes(debouncedSearch)) return false
    }
    return true
  })

  /** @param {string} key */
  function toggleSort(key) {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  /**
   * Sort key -> comparable value. Versions and status sort numerically (version
   * count, and urgency rank tie-broken by how much of the document is at that
   * rank) rather than by their rendered text.
   * @param {object} source
   * @param {string} key
   */
  function sortValue(source, key) {
    if (key === 'products') return [...(source.products ?? [])].sort().join(', ')
    if (key === 'name') return source.name
    if (key === 'versions') return (source.contentSets ?? []).length
    if (key === 'status') return statusSeverity(source.contentSets)
    if (key === 'updatedAt') return source.updatedAt
    return ''
  }

  const sorted = [...filtered].sort((a, b) => {
    const av = sortValue(a, sortKey)
    const bv = sortValue(b, sortKey)
    const cmp = typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv))
    return sortDir === 'asc' ? cmp : -cmp
  })

  const detailsTarget = detailsId === null ? null : sources.find(s => s.id === detailsId) ?? null
  const addVersionTarget = addVersionId === null ? null : sources.find(s => s.id === addVersionId) ?? null

  function toggleExpand(sourceId) {
    setExpandedIds(ids => (ids.includes(sourceId) ? ids.filter(id => id !== sourceId) : [...ids, sourceId]))
  }

  async function handleAdd(payload) {
    const { upload } = await addKnowledgeSource(payload)
    // Documents only: the metadata row and its first content-set now exist
    // (status 'pending'); upload the actual bytes straight to S3 using the
    // presigned POST just returned. If this throws, the row is left in place
    // with no file behind it — the error surfaces to the user, who can upload
    // that version again from the source's details.
    if (payload.type === 'document' && upload) {
      await uploadDocumentFile(upload, payload.file)
    }
    await load()
  }

  /** @param {{ productVersion: string, file: File }} payload */
  async function handleAddVersion(payload) {
    const target = addVersionTarget
    const { upload } = await addKnowledgeVersion(target.id, { productVersion: payload.productVersion, file: payload.file })
    await uploadDocumentFile(upload, payload.file)
    // Show the version that was just added rather than leaving it behind a
    // collapsed chevron.
    setExpandedIds(ids => (ids.includes(target.id) ? ids : [...ids, target.id]))
    await load()
  }

  /**
   * @param {number} sourceId
   * @param {{ name?: string, products?: string[], description?: string, reviewDate?: string }} payload
   */
  async function handleSaveDetails(sourceId, payload) {
    await updateKnowledgeSource(sourceId, payload)
    await load()
  }

  async function confirmDelete() {
    const target = deleteTarget
    setDeleteTarget(null)
    try {
      if (target.contentSet) {
        await deleteKnowledgeVersion(target.source.id, target.contentSet.productVersion)
      } else {
        await deleteKnowledgeSource(target.source.id)
      }
      setError(null)
    } catch (e) {
      // A 409 here means it started processing between render and confirm.
      setError(e.message)
    }
    await load()
  }

  function deleteDialogCopy() {
    const { source, contentSet } = deleteTarget
    // Already marked for deletion: the teardown stalled, so this is a retry
    // rather than a first request, and asking to confirm a fresh deletion would
    // misdescribe what the button does.
    const { retry } = deleteAffordance(contentSet ? [contentSet] : source.contentSets ?? [])
    if (contentSet) {
      const version = productVersionLabel(contentSet.productVersion)
      if (retry) {
        return {
          title: 'Retry Deletion',
          message: `Deletion of ${version} of "${source.name}" hasn't completed. Request it again? The version stays marked for deletion in the meantime, and nothing already removed is affected.`,
          confirmLabel: 'Retry Deletion',
        }
      }
      return {
        title: 'Delete Version',
        message: `Are you sure you want to mark ${version} of "${source.name}" for deletion? This action cannot be undone. That version will immediately stop being used for retrieval and will show as Pending Delete until removal is complete; the source's other versions are unaffected.`,
        confirmLabel: 'Delete Version',
      }
    }
    if (retry) {
      return {
        title: 'Retry Deletion',
        message: `Deletion of "${source.name}" hasn't completed. Request it again? The source stays marked for deletion in the meantime, and nothing already removed is affected.`,
        confirmLabel: 'Retry Deletion',
      }
    }
    const versionCount = (source.contentSets ?? []).length
    return {
      title: 'Confirm Deletion',
      message: `Are you sure you want to mark "${source.name}" for deletion? This removes ${versionCount === 1 ? 'its single version' : `all ${versionCount} versions`} and cannot be undone. The source will immediately stop being used for retrieval and will show as Pending Delete until removal is complete.`,
      confirmLabel: 'Mark for Deletion',
    }
  }

  return (
    <div className={styles.page}>

      {/* ── Page header ─────────────────────────────── */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Knowledge Base</h1>
          <p className={styles.pageSub}>Documents and web sources indexed for retrieval-augmented search</p>
        </div>
        <button type="button" className={styles.addSourceBtn} onClick={() => setAddOpen(true)}>
          + Add Source
        </button>
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {/* ── Metric cards ─────────────────────────────── */}
      <div className={styles.metricsRow}>
        {loading
          ? Array(3).fill(0).map((_, i) => <div key={i} className={styles.metricSkeleton} />)
          : <>
              <MetricCard
                name="Total Sources"
                value={stats.total}
                cardtype="number"
                icon="layers"
                valueSize="1.9rem"
                minHeight="110px"
              />
              <MetricCard
                name="Documents"
                value={stats.documents}
                cardtype="number"
                icon="document"
                valueColor="var(--color-semantic-blue)"
                valueSize="1.9rem"
                minHeight="110px"
              />
              <MetricCard
                name="Web Sources"
                value={stats.web}
                cardtype="number"
                icon="globe"
                valueColor="var(--color-semantic-green)"
                valueSize="1.9rem"
                minHeight="110px"
              />
            </>
        }
      </div>

      {/* ── Search + tabs + product filter ───────────── */}
      <div className={styles.controls}>
        <div className={styles.searchWrap}>
          <Icon name="search" className={styles.searchIcon} />
          <input
            className={styles.search}
            type="text"
            placeholder="Search by name, product, version, or URL"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Escape' && setSearch('')}
          />
          {search && (
            <button type="button" className={styles.clearSearch} onClick={() => setSearch('')}>
              Clear
            </button>
          )}
        </div>

        <div className={styles.tabs} role="tablist">
          {TABS.map(t => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className={styles.filterGroup}>
          <select
            className={styles.filterSelect}
            value={productFilter}
            onChange={e => setProductFilter(e.target.value)}
            aria-label="Filter by product"
          >
            <option value="all">All Products</option>
            {productOptions.map(name => <option key={name} value={name}>{name}</option>)}
          </select>
        </div>
      </div>

      {/* ── Source table (independently scrollable) ──── */}
      <div className={styles.tableScroll}>
        {loading
          ? <div className={styles.tableSkeleton} />
          : (
            <KnowledgeSourceTable
              sources={sorted}
              expandedIds={expandedIds}
              onToggleExpand={toggleExpand}
              onDelete={source => setDeleteTarget({ source })}
              onDeleteVersion={(source, contentSet) => setDeleteTarget({ source, contentSet })}
              onRowClick={source => setDetailsId(source.id)}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={toggleSort}
            />
          )
        }
      </div>

      <AddSourceModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        products={products}
        onSubmit={handleAdd}
      />

      <SourceDetailsModal
        source={detailsTarget}
        products={products}
        onClose={() => setDetailsId(null)}
        onSave={handleSaveDetails}
        onAddVersion={source => setAddVersionId(source.id)}
        onDeleteVersion={(source, contentSet) => setDeleteTarget({ source, contentSet })}
      />

      {/* After the details modal it opens from — same backdrop z-index, so DOM
          order is what puts it on top. Mounted only while targeted, so its form
          starts empty on every open. */}
      {addVersionTarget && (
        <AddVersionModal
          source={addVersionTarget}
          onClose={() => setAddVersionId(null)}
          onSubmit={handleAddVersion}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          {...deleteDialogCopy()}
          variant="danger"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

    </div>
  )
}
